import { useCallback, useEffect, useRef, useState } from 'react';

import type { ProficiencyLevel } from '@/types/competencies/ProficiencyLevel';
import type { CycleInstance } from '@/types/cycle-evaluation/CycleInstance';

import { useHydrationTracking } from '@/hooks/useDirtyState';
import { orderProficiencyLevelsWorstFirst } from '@/utils/proficiencyLevel';

// Stable serialization of the ratings so dirtiness is a comparison against the last-saved snapshot
// rather than a one-way flag — changing a value and setting it back to the original reads as clean.
const serializeAssessments = (assessments: Record<string, number>): string =>
    JSON.stringify(
        Object.keys(assessments)
            .filter((key) => assessments[key] !== undefined)
            .sort()
            .map((key) => [key, assessments[key]]),
    );

export const useQuickAssessment = (
    cycleInstance: CycleInstance | undefined,
    proficiencyLevels: ProficiencyLevel[],
) => {
    const [assessments, setAssessments] = useState<Record<string, number>>({});

    const { hydratedRef } = useHydrationTracking();
    // Snapshot of the last-saved (or hydrated) ratings; dirtiness compares current against it.
    const baselineRef = useRef(serializeAssessments({}));

    // Rehydrate from saved proficiency levels
    useEffect(() => {
        if (hydratedRef.current) {
            return;
        }
        if (!cycleInstance || proficiencyLevels.length === 0) {
            return;
        }
        const orderedLevels = orderProficiencyLevelsWorstFirst(proficiencyLevels);

        const nextAssessments: Record<string, number> = {};

        // Competencies
        cycleInstance.assessmentCompetencies?.forEach((competency) => {
            if (!competency.assessedProficiencyLevelId) {
                return;
            }
            const levelIndex = orderedLevels.findIndex(
                (level) => level.id === competency.assessedProficiencyLevelId,
            );
            if (levelIndex >= 0) {
                nextAssessments[competency.competencyId] = levelIndex;
            }
        });

        // Company values
        cycleInstance.assessmentCompanyValues?.forEach((value) => {
            if (!value.assessedProficiencyLevelId) {
                return;
            }
            const levelIndex = orderedLevels.findIndex(
                (level) => level.id === value.assessedProficiencyLevelId,
            );
            if (levelIndex >= 0) {
                nextAssessments[value.companyValueId] = levelIndex;
            }
        });

        setAssessments(nextAssessments);
        baselineRef.current = serializeAssessments(nextAssessments);
        hydratedRef.current = true;
    }, [cycleInstance, proficiencyLevels, hydratedRef]);

    const handleAssessmentChange = useCallback((itemId: string, levelIndex: number) => {
        setAssessments((prev) => ({ ...prev, [itemId]: levelIndex }));
    }, []);

    // Adopt the current ratings as the new baseline after a successful save.
    const markSaved = useCallback(() => {
        baselineRef.current = serializeAssessments(assessments);
    }, [assessments]);

    const isDirty = serializeAssessments(assessments) !== baselineRef.current;

    return {
        assessments,
        isDirty,
        handleAssessmentChange,
        setAssessments,
        markSaved,
    };
};
